import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { LocalVideoTrack } from 'twilio-video';
import {
  GaussianBlurBackgroundProcessor,
  ImageFit,
  VirtualBackgroundProcessor,
  isSupported as videoProcessorsSupported,
} from '@twilio/video-processors';
import useVideoContext from '../useVideoContext/useVideoContext';

export type BackgroundEffect = 'none' | 'blur' | 'image';

const ASSETS_PATH = '/twilio-video-processors';
const DEFAULT_BLUR_INTENSITY = 15;
const MIN_BLUR_INTENSITY = 1;
const MAX_BLUR_INTENSITY = 25;
const MIN_PROCESSOR_BLUR_INTENSITY = 1;

const normalizeBlurIntensity = (value: number) =>
  Math.min(MAX_BLUR_INTENSITY, Math.max(MIN_BLUR_INTENSITY, Math.round(value)));

const getProcessorBlurFilterRadius = (value: number) => Math.max(MIN_PROCESSOR_BLUR_INTENSITY, value);

export interface BackgroundEffectState {
  currentEffect: BackgroundEffect;
  blurIntensity: number;
  isSupported: boolean;
  isApplying: boolean;
  selectedImageName?: string;
  error?: string;
  applyBlur: () => Promise<void>;
  applyCustomBackground: (file: File) => Promise<void>;
  clearEffect: () => void;
  setBlurIntensity: (value: number) => void;
}

export default function useBackgroundEffect(): BackgroundEffectState {
  const { localTracks } = useVideoContext();

  const videoTrack = useMemo(() => localTracks.find((track): track is LocalVideoTrack => track.kind === 'video'), [
    localTracks,
  ]);

  const processorRef = useRef<GaussianBlurBackgroundProcessor | VirtualBackgroundProcessor | null>(null);
  const trackRef = useRef<LocalVideoTrack | null>(null);
  const [currentEffect, setCurrentEffect] = useState<BackgroundEffect>('none');
  const [blurIntensity, setBlurIntensityState] = useState(DEFAULT_BLUR_INTENSITY);
  const [isApplying, setIsApplying] = useState(false);
  const [selectedImageName, setSelectedImageName] = useState<string>();
  const [customBackgroundSrc, setCustomBackgroundSrc] = useState<string>();
  const [error, setError] = useState<string>();

  const updateCustomBackgroundSrc = useCallback(
    (nextSrc?: string) => {
      if (customBackgroundSrc && customBackgroundSrc !== nextSrc) {
        URL.revokeObjectURL(customBackgroundSrc);
      }
      setCustomBackgroundSrc(nextSrc);
    },
    [customBackgroundSrc]
  );

  const removeProcessor = useCallback(() => {
    if (processorRef.current && trackRef.current) {
      try {
        trackRef.current.removeProcessor(processorRef.current);
      } catch (e) {
        // Swallow errors from removeProcessor to avoid blocking UI updates.
      }
    }
    processorRef.current = null;
  }, []);

  useEffect(() => {
    const previousTrack = trackRef.current;

    if (previousTrack && processorRef.current && previousTrack !== videoTrack) {
      try {
        previousTrack.removeProcessor(processorRef.current);
      } catch (e) {
        // Ignore cleanup errors when switching tracks.
      }
    }

    if (videoTrack && processorRef.current) {
      videoTrack.addProcessor(processorRef.current);
    }

    trackRef.current = videoTrack ?? null;

    return () => {
      if (videoTrack && processorRef.current) {
        try {
          videoTrack.removeProcessor(processorRef.current);
        } catch (e) {
          // Ignore cleanup errors on unmount.
        }
      }
    };
  }, [videoTrack]);

  const clearEffect = useCallback(() => {
    removeProcessor();
    updateCustomBackgroundSrc(undefined);
    setSelectedImageName(undefined);
    setCurrentEffect('none');
    setError(undefined);
  }, [removeProcessor, updateCustomBackgroundSrc]);

  const setBlurIntensity = useCallback((value: number) => {
    const nextIntensity = normalizeBlurIntensity(value);
    setBlurIntensityState(nextIntensity);

    if (processorRef.current instanceof GaussianBlurBackgroundProcessor) {
      processorRef.current.blurFilterRadius = getProcessorBlurFilterRadius(nextIntensity);
    }
  }, []);

  const applyBlur = useCallback(async () => {
    if (!videoProcessorsSupported) {
      setError('Background effects are not supported on this device.');
      return;
    }
    if (!videoTrack) {
      setError('Camera is not available yet.');
      return;
    }

    if (processorRef.current instanceof GaussianBlurBackgroundProcessor) {
      processorRef.current.blurFilterRadius = getProcessorBlurFilterRadius(blurIntensity);
      setCurrentEffect('blur');
      updateCustomBackgroundSrc(undefined);
      setSelectedImageName(undefined);
      setError(undefined);
      return;
    }

    setIsApplying(true);
    const previousProcessor = processorRef.current;

    try {
      const blurProcessor = new GaussianBlurBackgroundProcessor({
        assetsPath: ASSETS_PATH,
        blurFilterRadius: getProcessorBlurFilterRadius(blurIntensity),
      });

      await blurProcessor.loadModel();

      if (previousProcessor) {
        videoTrack.removeProcessor(previousProcessor);
      }

      videoTrack.addProcessor(blurProcessor);
      processorRef.current = blurProcessor;
      setCurrentEffect('blur');
      updateCustomBackgroundSrc(undefined);
      setSelectedImageName(undefined);
      setError(undefined);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unable to apply background blur');
      // If applying fails, keep previous processor (if any) active.
      if (previousProcessor) {
        processorRef.current = previousProcessor;
      } else {
        clearEffect();
      }
    } finally {
      setIsApplying(false);
    }
  }, [blurIntensity, clearEffect, updateCustomBackgroundSrc, videoTrack]);

  const applyCustomBackground = useCallback(
    async (file: File) => {
      if (!videoProcessorsSupported) {
        setError('Background effects are not supported on this device.');
        return;
      }
      if (!videoTrack) {
        setError('Camera is not available yet.');
        return;
      }

      setIsApplying(true);
      const previousProcessor = processorRef.current;
      const backgroundSrc = URL.createObjectURL(file);
      const image = new Image();
      image.crossOrigin = 'anonymous';

      try {
        await new Promise<void>((resolve, reject) => {
          image.onload = () => resolve();
          image.onerror = () => reject(new Error('Unable to load background image'));
          image.src = backgroundSrc;
        });

        const backgroundProcessor = new VirtualBackgroundProcessor({
          assetsPath: ASSETS_PATH,
          backgroundImage: image,
          fitType: ImageFit.Cover,
        });

        await backgroundProcessor.loadModel();

        if (previousProcessor) {
          videoTrack.removeProcessor(previousProcessor);
        }

        videoTrack.addProcessor(backgroundProcessor);
        processorRef.current = backgroundProcessor;
        updateCustomBackgroundSrc(backgroundSrc);
        setSelectedImageName(file.name);
        setCurrentEffect('image');
        setError(undefined);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Unable to apply custom background');
        URL.revokeObjectURL(backgroundSrc);
        // Restore previous processor if there was one.
        if (previousProcessor) {
          processorRef.current = previousProcessor;
        } else {
          clearEffect();
        }
      } finally {
        setIsApplying(false);
      }
    },
    [clearEffect, updateCustomBackgroundSrc, videoTrack]
  );

  useEffect(
    () => () => {
      updateCustomBackgroundSrc(undefined);
    },
    [updateCustomBackgroundSrc]
  );

  return {
    currentEffect,
    blurIntensity,
    isSupported: videoProcessorsSupported,
    isApplying,
    selectedImageName,
    error,
    applyBlur,
    applyCustomBackground,
    clearEffect,
    setBlurIntensity,
  };
}
